import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Box, CheckCircle2, Clock3, MapPin, PackageCheck, Search, Truck, XCircle } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { useAuth } from "../context/AuthContext";

const statuses = ["Hazırlanıyor", "Onaylandı", "Kargoda", "Teslim Edildi", "İptal Edildi"];
const statusMeta = {
  "Hazırlanıyor": { icon: Clock3, badge: "bg-amber-50 text-amber-700 ring-amber-100" },
  "Onaylandı": { icon: CheckCircle2, badge: "bg-blue-50 text-blue-700 ring-blue-100" },
  "Kargoda": { icon: Truck, badge: "bg-indigo-50 text-indigo-700 ring-indigo-100" },
  "Teslim Edildi": { icon: PackageCheck, badge: "bg-emerald-50 text-emerald-700 ring-emerald-100" },
  "İptal Edildi": { icon: XCircle, badge: "bg-red-50 text-red-700 ring-red-100" },
};

function dateText(value) {
  return value ? new Date(value).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }) : "-";
}

function statusIndex(status) {
  return Math.max(0, statuses.indexOf(status));
}

function StatusBadge({ status }) {
  const meta = statusMeta[status] || statusMeta["Hazırlanıyor"];
  const Icon = meta.icon;
  return <span className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold ring-1 ${meta.badge}`}><Icon size={16} />{status}</span>;
}

function OrderTimeline({ status }) {
  const active = statusIndex(status);
  const visible = statuses.filter((s) => s !== "İptal Edildi");
  if (status === "İptal Edildi") return <div className="rounded-2xl bg-red-50 p-4 text-sm font-semibold text-red-700">Sipariş iptal edildi.</div>;
  return (
    <div className="grid gap-2 sm:grid-cols-4">
      {visible.map((step, index) => {
        const done = index <= active;
        return (
          <div key={step} className={`rounded-2xl border p-3 text-xs font-semibold ${done ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-stone-100 bg-stone-50 text-stone-400"}`}>
            <div className="mb-2 flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${done ? "bg-emerald-600" : "bg-stone-300"}`} />{step}</div>
            <div className="h-1 rounded-full bg-white"><div className={`h-1 rounded-full ${done ? "bg-emerald-600" : "bg-stone-200"}`} /></div>
          </div>
        );
      })}
    </div>
  );
}

export default function OrdersPage() {
  const { orders, updateOrderStatus } = useShop();
  const { user, isAdmin } = useAuth();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("Tümü");

  const filteredOrders = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("tr-TR");
    return orders.filter((order) => {
      const statusOk = filter === "Tümü" || order.status === filter;
      const text = [order.id, order.status, order.customerInfo?.fullName, order.customerInfo?.phone, order.customerInfo?.address, ...(order.items || []).map((i) => i.name)].join(" ").toLocaleLowerCase("tr-TR");
      return statusOk && (!q || text.includes(q));
    });
  }, [orders, query, filter]);

  const stats = useMemo(() => ({
    count: orders.length,
    active: orders.filter((o) => !["Teslim Edildi", "İptal Edildi"].includes(o.status)).length,
    delivered: orders.filter((o) => o.status === "Teslim Edildi").length,
  }), [orders]);

  if (!user) {
    return <section className="px-5 py-16 text-center md:px-10"><h1 className="mb-4 text-3xl font-bold">Siparişlerini görmek için giriş yap</h1><Link to="/login" className="rounded-xl bg-stone-900 px-6 py-3 text-white">Giriş Yap</Link></section>;
  }

  return (
    <section className="px-4 py-8 sm:px-6 md:px-10">
      <div className="mb-7 overflow-hidden rounded-[2rem] bg-gradient-to-br from-stone-950 via-stone-900 to-emerald-950 p-6 text-white shadow-soft sm:p-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.25em] text-emerald-200">Sevinç Outdoor</p>
            <h1 className="text-3xl font-black sm:text-4xl">{isAdmin ? "Gelen Siparişler" : "Siparişlerim"}</h1>
            <p className="mt-2 max-w-2xl text-sm text-stone-300">{isAdmin ? "Siparişleri tek ekrandan takip et, müşteri bilgilerini gör ve durum güncelle." : "Sipariş durumunu, ürünleri ve teslimat bilgisini profesyonel şekilde takip et."}</p>
          </div>
          {!isAdmin && <Link to="/products" className="w-fit rounded-2xl bg-white px-5 py-3 text-sm font-bold text-stone-950">Alışverişe Devam Et</Link>}
        </div>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <div className="rounded-3xl bg-white p-5 shadow-soft"><p className="text-sm text-stone-500">Toplam Sipariş</p><strong className="mt-1 block text-2xl">{stats.count}</strong></div>
        <div className="rounded-3xl bg-white p-5 shadow-soft"><p className="text-sm text-stone-500">Aktif Süreç</p><strong className="mt-1 block text-2xl">{stats.active}</strong></div>
        <div className="rounded-3xl bg-white p-5 shadow-soft"><p className="text-sm text-stone-500">Teslim Edilen</p><strong className="mt-1 block text-2xl">{stats.delivered}</strong></div>
      </div>

      <div className="mb-6 flex flex-col gap-3 rounded-3xl bg-white p-4 shadow-soft lg:flex-row lg:items-center lg:justify-between">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Sipariş no, ürün, müşteri veya adres ara" className="w-full rounded-2xl border border-stone-200 py-3 pl-11 pr-4 outline-none focus:border-emerald-600" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-2xl border border-stone-200 bg-white px-4 py-3 outline-none focus:border-emerald-600">
          {["Tümü", ...statuses].map((status) => <option key={status}>{status}</option>)}
        </select>
      </div>

      {!filteredOrders.length ? (
        <div className="rounded-3xl bg-white p-10 text-center shadow-soft">
          <Box className="mx-auto mb-4 text-stone-400" size={44} />
          <h2 className="text-xl font-bold">Sipariş bulunamadı</h2>
          <p className="mt-2 text-stone-500">Filtreyi değiştirerek tekrar deneyebilirsin.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {filteredOrders.map((order) => (
            <article key={order.id} className="overflow-hidden rounded-[1.8rem] bg-white shadow-soft ring-1 ring-stone-100">
              <div className="flex flex-col gap-4 border-b border-stone-100 p-5 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-3"><h3 className="text-xl font-black">Sipariş #{order.id}</h3><StatusBadge status={order.status} /></div>
                  <p className="mt-1 text-sm text-stone-500">Oluşturulma: {dateText(order.createdAt)}</p>
                </div>
                {isAdmin ? (
                  <select value={order.status} onChange={(e) => updateOrderStatus(order.id, e.target.value).catch((err) => alert(err.message))} className="w-fit rounded-2xl border bg-white px-4 py-3 font-semibold">
                    {statuses.map((status) => <option key={status}>{status}</option>)}
                  </select>
                ) : <Link to="/products" className="w-fit rounded-2xl bg-stone-100 px-4 py-3 text-sm font-bold text-stone-700">Tekrar Alışveriş Yap</Link>}
              </div>

              <div className="grid gap-6 p-5 xl:grid-cols-[1.2fr_0.8fr]">
                <div className="space-y-4">
                  <OrderTimeline status={order.status} />
                  <div className="space-y-3">
                    {(order.items || []).map((item) => (
                      <div key={item.id} className="flex gap-4 rounded-3xl bg-stone-50 p-3">
                        <img src={item.image} alt={item.name} className="h-20 w-20 rounded-2xl object-cover" />
                        <div className="min-w-0 flex-1">
                          <h4 className="font-bold text-stone-950">{item.name}</h4>
                          <p className="text-sm text-stone-500">{item.type} · {item.quantity} adet</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <aside className="space-y-3">
                  {isAdmin && <div className="rounded-3xl bg-stone-50 p-4"><p className="mb-2 text-xs font-bold uppercase tracking-widest text-stone-400">Müşteri</p><p className="font-bold">{order.customerInfo?.fullName || "-"}</p><p className="text-sm text-stone-500">{order.customerInfo?.phone || "-"}</p><p className="text-xs text-stone-400">Kullanıcı ID: {order.userId}</p></div>}
                  <div className="rounded-3xl bg-stone-50 p-4"><p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-stone-400"><MapPin size={15} />Teslimat</p><p className="text-sm leading-6 text-stone-700">{order.customerInfo?.address || "Adres bilgisi yok"}</p></div>
                  <div className="rounded-3xl border border-stone-100 p-4">
                    <p className="mb-2 text-xs font-bold uppercase tracking-widest text-stone-400">Sipariş Özeti</p>
                    <div className="flex justify-between text-sm"><span>Ürün çeşidi</span><strong>{(order.items || []).length}</strong></div>
                    <div className="mt-2 flex justify-between text-sm"><span>Toplam adet</span><strong>{(order.items || []).reduce((sum, item) => sum + Number(item.quantity || 0), 0)}</strong></div>
                    <p className="mt-4 rounded-2xl bg-white p-3 text-xs leading-5 text-stone-500">Fiyat ve toplam tutar bilgileri bu sayfada gösterilmez; sipariş operasyonu ürün, adet, teslimat ve durum takibi üzerinden yürütülür.</p>
                  </div>
                </aside>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
